import { ReactNode } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { formatDate } from '@frontend/date';
import { i18next, Trans } from '@frontend/i18n';
import { CreditUsageVariant, LocationCreditData, SortDirection, SortField } from './types';

dayjs.extend(advancedFormat);

export function getAIRCreditUsagePercent(consumedCredits: number, totalCredits: number): number {
  if (totalCredits === 0) return 0;
  return (consumedCredits / totalCredits) * 100;
}

export function getAIRCreditUsageVariant(usagePercent: number): CreditUsageVariant {
  if (usagePercent >= 100) return 'exhausted';
  if (usagePercent >= 95) return 'critical';
  if (usagePercent >= 80) return 'warning';
  return 'normal';
}

export function sortLocationCredits(
  locations: LocationCreditData[],
  field: SortField = 'usage',
  direction: SortDirection = 'desc'
): LocationCreditData[] {
  const sorted = [...locations].sort((a, b) => {
    let comparisonValue = 0;
    switch (field) {
      case 'locationName':
        comparisonValue = a.locationName.localeCompare(b.locationName);
        break;
      case 'usage':
        comparisonValue = a.usagePercent - b.usagePercent;
        break;
    }
    return direction === 'asc' ? comparisonValue : -comparisonValue;
  });
  return sorted;
}

export interface AICallCreditAlertContent {
  title: string;
  description: ReactNode;
  ctaLabel: string;
  severity: CreditUsageVariant;
  bannerType: 'error' | 'warning';
}

/**
 * Builds a display string for the title field (plain string, no JSX).
 * Rules:
 *   1 location  → "Location Name"
 *   2 locations → "Location A and Location B"
 *   3+ locations → "Location A and N more"
 */
function LocationTitleComponent({ locations }: { locations: LocationCreditData[] }): ReactNode {
  const count = locations.length;
  const firstLocationName = locations[0].locationName;
  if (locations.length === 1)
    return (
      <Trans t={i18next.t} ns={'subscription>>ai-receptionist'}>
        <strong>{firstLocationName}</strong>
      </Trans>
    );
  if (locations.length === 2) {
    const secondLocationName = locations[1].locationName;
    return (
      <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} values={{ firstLocationName, secondLocationName }}>
        <strong>{{ firstLocationName }}</strong> & <strong>{{ secondLocationName }}</strong>
      </Trans>
    );
  }
  return (
    <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} count={count}>
      <strong>{{ count }} locations</strong>
    </Trans>
  );
}

const isForecastEndBeforeBillingEnd = (loc: LocationCreditData) => {
  const forecastedEndDate = loc.forecastedEndDate ? dayjs(loc.forecastedEndDate) : undefined;
  const billingEndDate = loc.billingEndDate ? dayjs(loc.billingEndDate) : undefined;
  return forecastedEndDate && billingEndDate ? forecastedEndDate.isBefore(billingEndDate) : false;
};

/**
 * Computes the alert title, description, CTA label, and banner type for the highest-severity
 * credit alert across all affected locations. Used by both the global banner and the
 * subscription page inline alert to keep messaging in sync.
 *
 * Returns null when there are no locations to alert on.
 */
export function getAICallCreditAlertContent({
  exhaustedLocations,
  criticalLocations,
  warningLocations,
  isMultiLocation,
}: {
  exhaustedLocations: LocationCreditData[];
  criticalLocations: LocationCreditData[];
  warningLocations: LocationCreditData[];
  isMultiLocation: boolean;
}): AICallCreditAlertContent | null {
  // --- Exhausted (100%) ---
  if (exhaustedLocations.length > 0) {
    const locations = exhaustedLocations;

    const exhaustedDate = locations[0].creditExhaustedAt ? formatDate(locations[0].creditExhaustedAt, 'MMM Do') : '';
    const nextBillingDate = locations[0].billingEndDate ? formatDate(locations[0].billingEndDate, 'MMM Do') : '';

    // Single Location
    if (!isMultiLocation) {
      return {
        title: i18next.t('You are out of AI Receptionist Call Credits', { ns: 'subscription>>ai-receptionist' }),
        description: exhaustedDate ? (
          <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} values={{ exhaustedDate, nextBillingDate }}>
            <LocationTitleComponent locations={locations} /> reached the limit for calls handled by AI Receptionist on{' '}
            <strong>{{ exhaustedDate }}</strong>. Calls are now being directed based on your call route settings until
            your billing cycle refreshes on <strong>{{ nextBillingDate }}</strong>. Upgrade your subscription today to
            continue having your AI Receptionist answer incoming calls.
          </Trans>
        ) : (
          <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} values={{ exhaustedDate, nextBillingDate }}>
            <LocationTitleComponent locations={locations} /> reached the limit for calls handled by AI Receptionist.
            Calls are now being directed based on your call route settings until your billing cycle refreshes on{' '}
            <strong>{{ nextBillingDate }}</strong>. Upgrade your subscription today to continue having your AI
            Receptionist answer incoming calls.
          </Trans>
        ),
        ctaLabel: i18next.t('Add Call Credits', { ns: 'subscription>>ai-receptionist' }),
        severity: 'exhausted',
        bannerType: 'error',
      };
    }

    // Multi Location

    return {
      title: i18next.t('{{count}} locations are out of AI Receptionist Call Credits', {
        count: locations.length,
        ns: 'subscription>>ai-receptionist',
      }),
      description: (
        <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} count={locations.length}>
          <LocationTitleComponent locations={locations} /> reached the limit for calls handled by AI Receptionist. Calls
          are now being directed based on your call route settings. Upgrade your subscription today to continue having
          your AI Receptionist answer incoming calls.
        </Trans>
      ),
      ctaLabel: i18next.t('Add Call Credits', { ns: 'subscription>>ai-receptionist' }),
      severity: 'exhausted',
      bannerType: 'error',
    };
  }

  // --- Critical (95–99%) ---
  if (criticalLocations.length > 0) {
    const locations = criticalLocations;
    const firstLoc = locations[0];
    const firstLocationName = firstLoc.locationName;
    const remainingCredits = Math.max(firstLoc.totalCredits - firstLoc.consumedCredits, 0);

    const forecastedEndDate = firstLoc.forecastedEndDate
      ? dayjs(firstLoc.forecastedEndDate).format('MMM Do')
      : undefined;
    const showForecast = isForecastEndBeforeBillingEnd(firstLoc);
    const nextBillingDate = firstLoc.billingEndDate ? formatDate(firstLoc.billingEndDate, 'MMM Do') : '';

    // Single Location
    if (!isMultiLocation) {
      return {
        title: i18next.t('You have 5% or fewer AI Receptionist Call Credits remaining', {
          remainingCredits,
          ns: 'subscription>>ai-receptionist',
        }),
        description: showForecast ? (
          <Trans
            t={i18next.t}
            ns={'subscription>>ai-receptionist'}
            values={{ firstLocationName, forecastedEndDate, nextBillingDate }}
          >
            {{ firstLocationName }} is about to reach the limit for calls handled by AI Receptionist. According to
            current usage trends, you will run out of call credits on <strong>{{ forecastedEndDate }}</strong>, before
            your billing cycle refreshes on <strong>{{ nextBillingDate }}</strong>. Once the limit is reached, new calls
            will be redirected based on your call route settings.
          </Trans>
        ) : (
          <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} values={{ firstLocationName }}>
            {{ firstLocationName }} is about to reach the limit for calls handled by AI Receptionist. Once the limit is
            reached, new calls will be redirected based on your call route settings.
          </Trans>
        ),
        ctaLabel: i18next.t('Review Usage', { ns: 'subscription>>ai-receptionist' }),
        severity: 'critical',
        bannerType: 'warning',
      };
    }

    // Multi Location
    const showForecastMulti = locations.some(isForecastEndBeforeBillingEnd);

    return {
      title: i18next.t('{{count}} locations have 5% or fewer AI Receptionist Call Credits remaining', {
        count: locations.length,
        ns: 'subscription>>ai-receptionist',
      }),
      description: showForecastMulti ? (
        <Trans
          t={i18next.t}
          ns={'subscription>>ai-receptionist'}
          values={{ count: locations.length }}
          count={locations.length}
        >
          <LocationTitleComponent locations={locations} /> are about to reach the limit for calls handled by AI
          Receptionist. According to their current usage trends, credits will run out before your billing cycle
          refreshes. Once the limit is reached, calls will skip the AI Receptionist in your call routing
        </Trans>
      ) : (
        <Trans
          t={i18next.t}
          ns={'subscription>>ai-receptionist'}
          values={{ count: locations.length }}
          count={locations.length}
        >
          <LocationTitleComponent locations={locations} /> are about to reach the limit for calls handled by AI
          Receptionist. Once the limit is reached, calls will skip the AI Receptionist in your call routing
        </Trans>
      ),
      ctaLabel: i18next.t('Review Usage', { ns: 'subscription>>ai-receptionist' }),
      severity: 'critical',
      bannerType: 'warning',
    };
  }

  // --- Warning (80–94%) ---
  if (warningLocations.length > 0) {
    const locations = warningLocations;

    // Single Location
    if (!isMultiLocation) {
      const firstLoc = locations[0];
      const locationName = firstLoc.locationName;
      const remainingCredits = Math.max(firstLoc.totalCredits - firstLoc.consumedCredits, 0);

      const forecastedEndDate = firstLoc.forecastedEndDate
        ? dayjs(firstLoc.forecastedEndDate).format('MMM Do')
        : undefined;
      const showForecast = isForecastEndBeforeBillingEnd(firstLoc);
      const nextBillingDate = firstLoc.billingEndDate ? formatDate(firstLoc.billingEndDate, 'MMM Do') : '';

      return {
        title: i18next.t('{{count}} AI Receptionist Call Credits left', {
          count: remainingCredits,
          ns: 'subscription>>ai-receptionist',
        }),
        description: showForecast ? (
          <Trans
            t={i18next.t}
            ns={'subscription>>ai-receptionist'}
            values={{ locationName, forecastedEndDate, remainingCredits, nextBillingDate }}
          >
            <strong>{{ locationName }}</strong> has used 80% or more of its call credits. According to your current
            usage trends, credits will run out by <strong>{{ forecastedEndDate }}</strong>, before your billing cycle
            refreshes on <strong>{{ nextBillingDate }}</strong>. Calls will be redirected based on your call route
            settings once the limit is reached.
          </Trans>
        ) : (
          <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} values={{ locationName, remainingCredits }}>
            <strong>{{ locationName }}</strong> has used 80% or more of its call credits. Calls will be redirected based
            on your call route settings once the limit is reached.
          </Trans>
        ),
        ctaLabel: i18next.t('Review Usage', { ns: 'subscription>>ai-receptionist' }),
        severity: 'warning',
        bannerType: 'warning',
      };
    }

    // Multi Location
    const showForecastMulti = locations.some(isForecastEndBeforeBillingEnd);

    return {
      title: i18next.t('{{count}} locations have 20% AI Receptionist Call Credits or fewer left', {
        count: locations.length,
        ns: 'subscription>>ai-receptionist',
      }),
      description: showForecastMulti ? (
        <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} count={locations.length}>
          <LocationTitleComponent locations={locations} /> have used <strong>80%</strong> or more of their call credits.
          According to current usage trends, credits will run out for each location before its next billing cycle
          refresh. Calls will be redirected based on your call route settings once the limit is reached.
        </Trans>
      ) : (
        <Trans t={i18next.t} ns={'subscription>>ai-receptionist'} count={locations.length}>
          <LocationTitleComponent locations={locations} /> have used <strong>80%</strong> or more of their call credits.
          Calls will be redirected based on your call route settings once the limit is reached.
        </Trans>
      ),
      ctaLabel: i18next.t('Review Usage', { ns: 'subscription>>ai-receptionist' }),
      severity: 'warning',
      bannerType: 'warning',
    };
  }

  return null;
}

/** Returns the messaging AI quota linked to a given Voice AI call credit quota. */
export function getMessagingQuotaForVoice(voiceQuota: number): number {
  switch (voiceQuota) {
    case 100:
      return 1000;
    case 200:
      return 2000;
    default:
      return 5000;
  }
}
